import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { formatTime } from '@/lib/format';
import { toIsoDate } from '@/lib/timezone';
import {
  SLOT_MINUTES,
  gridRows,
  packLanes,
  slotCount,
  slotInstants,
  type BoardDay,
  type VisibleRange,
} from '@/lib/dispatchBoard';
import type { ConflictMap } from '@/lib/conflicts';
import { availabilityOn, isUnavailable, type TechnicianBoardData } from '@/lib/availability';
import type { TechnicianDetailStatus, UserSummary } from '@/routes/dispatch/useDispatchBoard';
import { jobCount, type BoardItem } from '@/lib/boardItems';
import { JobCard } from './JobCard';
import { TechnicianTooltip } from './TechnicianTooltip';
import type { JobDragBinding } from './dragBinding';

/** Height of one half-hour row. Fixed so the time axis and every column agree. */
const SLOT_HEIGHT_REM = 2.25;

/**
 * Technician columns against a time axis — the day view (M1-09).
 *
 * <b>The grid is drawn, not announced.</b> A dispatcher sees columns crossed
 * with half-hour rows; a screen reader gets one landmark per technician
 * containing an ordinary list of that technician's jobs, each with its own
 * time. The alternative — a real `role="grid"` of 25 columns by 20 rows — is
 * 500 cells of which perhaps thirty hold anything, and navigating it cell by
 * cell to find the day's six jobs is technically conformant and practically
 * unusable. The gridlines are `aria-hidden` because they carry no information
 * the times on the cards do not already carry.
 *
 * Rows are half-hour SLOTS of elapsed time, not wall-clock hours, and their
 * labels are formatted from the instant each slot actually begins. On the day
 * the clocks go forward that makes the axis read 01:30 then 03:00, which is
 * what the technicians' own clocks will do.
 */
export function DayBoard({
  day,
  range,
  technicians,
  jobs,
  timeZone,
  dragBinding,
  conflicts,
  board,
  detailStatus,
}: {
  day: BoardDay;
  range: VisibleRange;
  technicians: UserSummary[];
  /** One per visit, plus one per job with none (M2-52): see `boardItems`. */
  jobs: BoardItem[];
  timeZone: string;
  dragBinding: JobDragBinding;
  conflicts: ConflictMap;
  /** Availability, licences and skills (M1-39). Undefined until the one board request lands. */
  board: TechnicianBoardData | undefined;
  /** Whether that request is still in flight or came back empty-handed (M1-39). */
  detailStatus: TechnicianDetailStatus;
}) {
  const { t } = useTranslation();
  // Which column's tooltip is open, so its heading can be lifted out of the
  // way of the ones after it. See `TechnicianTooltip`'s `onOpenChange`.
  const [openPanelFor, setOpenPanelFor] = useState<string | null>(null);
  const slots = slotInstants(day.dayStart, range);
  const rows = slotCount(range);
  const gridStyle = {
    gridTemplateRows: `repeat(${rows}, ${SLOT_HEIGHT_REM}rem)`,
  } as const;

  /** Minutes after midnight at which a slot begins — for the outside-hours shading. */
  const slotMinute = (index: number) => range.startMinute + index * SLOT_MINUTES;

  const isOutsideHours = (index: number) => {
    if (range.openMinute === null || range.closeMinute === null) {
      // No hours recorded for this weekday: the business is closed all day, and
      // the header says so. Shading every row as well would be noise.
      return false;
    }
    const minute = slotMinute(index);
    return minute < range.openMinute || minute >= range.closeMinute;
  };

  return (
    <div
      role="region"
      aria-labelledby="dispatch-board-heading"
      className="overflow-x-auto rounded-lg border"
      data-testid="day-board"
      // WCAG 2.1.1 (M1-42), and this one measurably overflows. Each technician
      // column is `w-44` (176px) beside a 64px time axis, so the board outruns its
      // container from about four technicians up — measured in Chrome at a 768px
      // viewport with six: 1120px of content in 719px, 401px off the right edge,
      // and `scrollLeft` reaches every pixel of it.
      //
      // ⚠️ Tabbing through the column HEADINGS scrolls the board incidentally —
      // there were six focusable descendants in that same measurement, one per
      // column — which is why this looked reachable and was not. That route is a
      // side effect of the headings being tooltip triggers, not of the container
      // being reachable, and it moves in 176px jumps rather than letting anyone
      // read what is between. Focusing the container is the part that does not
      // depend on what the board happens to contain.
      //
      // `<div role="region">` rather than a named `<section>` for the lint reason
      // recorded in eslint.config.js; the computed role is the same.
      tabIndex={0}
    >
      {/* Visually redundant — a dispatcher can see this is the board — but it
          gives the region a name and keeps the technician columns' own
          headings at h3 under an h2, rather than jumping a level from the
          page's h1. Axe checks the second part; the first is why it is a real
          heading rather than an aria-label. */}
      <h2 id="dispatch-board-heading" className="sr-only">
        {t('dispatch.boardHeading')}
      </h2>
      <div className="flex min-w-max">
        {/* The time axis. Sticky so it stays readable once 25 columns are
            scrolled sideways past it. */}
        <div className="sticky left-0 z-10 w-16 shrink-0 border-r bg-[var(--color-background)]">
          <div className="flex h-12 items-end justify-end border-b px-2 pb-1">
            <span className="sr-only">{t('dispatch.timeAxisLabel')}</span>
          </div>
          <div className="grid" style={gridStyle} aria-hidden="true">
            {slots.map((slot, index) => (
              <div
                key={slot.toISOString()}
                className={cn(
                  'border-t px-2 pt-0.5 text-right text-[0.625rem] text-[var(--color-muted-foreground)]',
                  index === 0 && 'border-t-0',
                  isOutsideHours(index) && 'bg-[var(--color-muted)]',
                )}
              >
                {/* Only label the hour marks — a label every half hour is
                    unreadable at this row height. */}
                {slotMinute(index) % 60 === 0 ? formatTime(slot, timeZone) : null}
              </div>
            ))}
          </div>
        </div>

        {technicians.map((technician) => {
          const theirs = jobs.filter((job) => job.assignedUserId === technician.id);
          const placed = packLanes(theirs, day.dayStart, day.dayEnd);
          const headingId = `dispatch-tech-${technician.id}`;
          const dayState = board && availabilityOn(board.availability, technician.id, day.date);
          const unavailable = isUnavailable(dayState);
          // ⚠️ Unknown is deliberately NOT greyed — but it is not silent
          // either. An office that filled four templates in and missed the
          // fifth got no signal at all: on a Saturday the four correctly grey
          // and the fifth simply looks like the one person working the
          // weekend, which is the inverse of the truth. Nothing warns about
          // scheduling onto them either, because `detectConflicts` skips
          // Unknown for the same good reason the greying does. A word is the
          // whole fix; greying would be the day-one disaster this state exists
          // to avoid.
          const hoursNotSet = Boolean(board) && dayState?.availabilityState === 'Unknown';

          return (
            <section
              key={technician.id}
              aria-labelledby={headingId}
              className="w-44 shrink-0 border-r last:border-r-0"
              data-testid={`tech-section-${technician.id}`}
              // The greying is a `data-*` attribute as well as a class,
              // because jsdom applies no stylesheet and can therefore see a
              // Tailwind class only as a string. This is the assertion the
              // tests make, and the browser pass is what confirms it looks
              // like anything.
              data-unavailable={unavailable ? 'true' : undefined}
            >
              <h3
                id={headingId}
                className={cn(
                  // ⚠️ `z-20` is load-bearing, and NOT about the header itself.
                  // `position: sticky` creates a stacking context, so the
                  // tooltip panel's own `z-30` is scoped INSIDE this heading
                  // and cannot lift it above the job list — which is a later
                  // sibling and therefore wins on DOM order. Without this the
                  // panel renders, contains every word, and is painted over by
                  // the cards: invisible to jsdom, which has no stylesheet and
                  // no compositing, so every test still passed. Found in the
                  // browser by probing `elementFromPoint` down the open panel.
                  'sticky top-0 z-20 flex h-12 flex-col justify-center border-b bg-[var(--color-background)] px-2',
                  // Above the neighbouring columns' headings while this one
                  // holds an open panel. The day board's own trap was the job
                  // list; this is the same class of collision one axis over,
                  // and the week board cannot be fixed any other way.
                  openPanelFor === technician.id && 'z-40',
                  // Greyed, never hidden and never disabled — the column still
                  // takes drops, because M1-12's rule is that the board warns
                  // and dispatch decides.
                  unavailable && 'bg-[var(--color-muted)]',
                )}
              >
                <TechnicianTooltip
                  technicianId={technician.id}
                  name={technician.name}
                  summary={board?.details.get(technician.id)}
                  detailStatus={detailStatus}
                  dayState={dayState}
                  // No `dayLabel`: the day board shows exactly one date and
                  // its own navigation already names it.
                  activePanelId={openPanelFor}
                  onOpenChange={(open) =>
                    setOpenPanelFor((current) =>
                      open ? technician.id : current === technician.id ? null : current,
                    )
                  }
                />
                <span className="text-[0.625rem] text-[var(--color-muted-foreground)]">
                  {/* Jobs, not cards: a three-day repipe is one job (M2-52). */}
                  {t('dispatch.jobCount', { count: jobCount(theirs) })}
                  {hoursNotSet ? (
                    <>
                      {' · '}
                      <span className="text-[var(--color-muted-foreground)]">
                        {t('dispatch.availability.hoursNotSet')}
                      </span>
                    </>
                  ) : null}
                  {unavailable ? (
                    <>
                      {' · '}
                      {/* A word, not only a shade. The greying is the glance;
                          this is what a colourblind dispatcher and a screen
                          reader actually get, and it is the same
                          words-carry-the-meaning rule M1-09 and M1-12 both
                          settled on. */}
                      <span className="text-[var(--color-muted-foreground)]">
                        {t(
                          dayState?.availabilityState === 'TimeOff'
                            ? 'dispatch.availability.offToday'
                            : 'dispatch.availability.notScheduledToday',
                        )}
                      </span>
                    </>
                  ) : null}
                </span>
              </h3>

              <div className="relative">
                <div className="grid" style={gridStyle} aria-hidden="true">
                  {slots.map((slot, index) => (
                    <div
                      key={slot.toISOString()}
                      className={cn(
                        'border-t',
                        index === 0 && 'border-t-0',
                        // A half-hour line is lighter than an hour line, so the
                        // eye can still find the hour without a label on it.
                        slotMinute(index) % 60 !== 0 && 'border-dotted',
                        isOutsideHours(index) && 'bg-[var(--color-muted)]',
                      )}
                    />
                  ))}
                </div>

                {/* The real content: a list, laid over the drawn grid. A `<ul>`
                    rather than more grid cells, so assistive technology reads
                    "3 jobs" instead of walking forty empty ones. `display: grid`
                    on a list keeps its list semantics — it is `display: contents`
                    that strips them, which is why this needs no explicit role. */}
                {/* The drop surface. The whole column body is one target and
                    the slot is worked out from where in it the pointer landed
                    (see `useJobDrag`) — 25 rows of individual drop zones per
                    technician would be 600 elements to keep in sync for no
                    gain. The data-* attributes carry everything that
                    calculation needs, so it can read the DOM at drop time
                    rather than tracking geometry in React state that goes
                    stale the moment anything scrolls. */}
                <ul
                  className="absolute inset-0 grid p-px"
                  style={gridStyle}
                  data-testid={`tech-column-${technician.id}`}
                  data-drop-target=""
                  data-drop-technician={technician.id}
                  data-drop-date={toIsoDate(day.date)}
                  data-drop-slot-height={SLOT_HEIGHT_REM * 16}
                  data-drop-start-minute={range.startMinute}
                  data-drop-slot-minutes={SLOT_MINUTES}
                >
                  {placed.map(({ job, span, lane, laneCount }) => {
                    const { start, end } = gridRows(span, range);
                    return (
                      <li
                        key={job.id}
                        className="min-h-0 p-0.5"
                        style={{
                          gridRow: `${start} / ${end}`,
                          gridColumn: 1,
                          // Overlapping jobs share the column rather than
                          // covering each other. Percentages of the grid area,
                          // so this holds at any column width.
                          marginLeft: `${(lane / laneCount) * 100}%`,
                          width: `${100 / laneCount}%`,
                        }}
                      >
                        <JobCard
                          job={job}
                          timeZone={timeZone}
                          isDragging={dragBinding.draggingCardId === job.id}
                          handlers={dragBinding.handlersFor(job.id)}
                          conflicts={conflicts.get(job.id)}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
